/**
 * PortalCotizaciones — pestaña "Mis cotizaciones" del Portal de Clientes.
 *
 * Listado con filtros, detalle y respuesta (aprobar / rechazar / solicitar cambios).
 * Los datos llegan por RPC que la base filtra según la identidad autenticada; los
 * importes vienen calculados por la base y no se pueden editar aquí. Cada
 * respuesta pide confirmación explícita y viaja con la huella del contenido que
 * el cliente vio, para que no pueda aceptar un precio distinto al revisado.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ChevronLeft, Loader, AlertCircle, CheckCircle2, FileText, Calendar,
  Check, X, MessageSquare, FolderOpen, Clock,
} from "lucide-react";
import {
  ESTADOS_COTIZACION, FILTROS_COTIZACION, ACCIONES_RESPUESTA, filtrarCotizaciones, fmtCLP,
  detalleCotizacion, responderCotizacion, notificarRespuesta, mensajeErrorCotizacion,
} from "../../lib/cotizacionesPortal";
import { fmtFecha, fmtFechaHora } from "../../lib/formato";

// Mismo acento del resto del portal. Cambiar aquí si se redefine la paleta.
const ACCENT = "#29abe2", ACCENT_L = "#e8f5fc";
const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:ACCENT, accentL:ACCENT_L,
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5", err:"#ef4444", errL:"#fee2e2", warn:"#f59e0b", warnL:"#fef3c7",
  purple:"#6366f1", purpleL:"#eef2ff",
};
const font = "'DM Sans',sans-serif";
const card = { background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:"0 1px 3px #0000000a" };

function Estado({ estado }) {
  const e = ESTADOS_COTIZACION[estado] || { label: estado, color: C.sub, bg: "#f1f5f9" };
  return <span style={{padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:700, color:e.color, background:e.bg, whiteSpace:"nowrap"}}>{e.label}</span>;
}

// ── Listado ──────────────────────────────────────────────────────────────────
export default function PortalCotizaciones({ cotizaciones = [], cargando = false, error = "", abrirId = null, onActualizar }) {
  const [filtro, setFiltro] = useState("todas");
  const [abierta, setAbierta] = useState(abrirId);
  const [aviso, setAviso] = useState(null);

  useEffect(() => { if (abrirId) setAbierta(abrirId); }, [abrirId]);
  // Al primer llenado, si hay pendientes de revisión se parte mostrándolas.
  const inicial = useRef(false);
  useEffect(() => {
    if (inicial.current || !cotizaciones.length) return;
    inicial.current = true;
    if (cotizaciones.some(c => c.estado === "Pendiente")) setFiltro("Pendiente");
  }, [cotizaciones]);

  const visibles = useMemo(() => filtrarCotizaciones(cotizaciones, filtro), [cotizaciones, filtro]);
  const cuenta = (f) => f === "todas" ? cotizaciones.length : cotizaciones.filter(c => c.estado === f).length;

  if (abierta) {
    return <DetalleCotizacion id={abierta} onVolver={() => { setAbierta(null); onActualizar?.(); }}
      onCambio={onActualizar} onAviso={setAviso} aviso={aviso}/>;
  }

  return (
    <div>
      {aviso && <Aviso aviso={aviso} onCerrar={() => setAviso(null)}/>}
      <div role="tablist" aria-label="Filtrar cotizaciones" style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:14}}>
        {FILTROS_COTIZACION.map(f => (
          <button key={f.id} role="tab" aria-selected={filtro === f.id} onClick={() => setFiltro(f.id)}
            style={{padding:"7px 13px", borderRadius:20, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:font,
              border:`1px solid ${filtro === f.id ? C.accent : C.border}`,
              background: filtro === f.id ? C.accent : C.white, color: filtro === f.id ? "#fff" : C.sub}}>
            {f.label} <span style={{opacity:.8}}>({cuenta(f.id)})</span>
          </button>
        ))}
      </div>

      {error && <Aviso aviso={{ tipo:"error", texto:error }}/>}
      {cargando && !cotizaciones.length ? (
        <div style={{...card, padding:28, textAlign:"center", color:C.sub, fontSize:13}}><Loader size={14} className="spinning"/> Cargando cotizaciones…</div>
      ) : !cotizaciones.length ? (
        <div style={{...card, padding:"36px 20px", textAlign:"center", color:C.sub, fontSize:14}}>
          <FileText size={30} color={C.light} style={{margin:"0 auto 10px"}} aria-hidden="true"/>
          <div style={{fontWeight:700, color:C.text, marginBottom:4}}>Aún no tienes cotizaciones</div>
          Cuando el equipo de TuAplicacion te asigne una cotización, la verás aquí para revisarla y autorizarla.
        </div>
      ) : !visibles.length ? (
        <div style={{...card, padding:"28px 20px", textAlign:"center", color:C.sub, fontSize:13}}>No hay cotizaciones en este filtro.</div>
      ) : (
        <div style={{display:"grid", gap:10}}>
          {visibles.map(c => (
            <button key={c.id} type="button" onClick={() => setAbierta(c.id)} aria-label={`Abrir cotización ${c.id}`}
              style={{...card, padding:"14px 16px", textAlign:"left", cursor:"pointer", fontFamily:font, color:C.text,
                borderLeft:`4px solid ${(ESTADOS_COTIZACION[c.estado] || {}).color || C.border}`}}>
              <div style={{display:"flex", justifyContent:"space-between", gap:10, flexWrap:"wrap", alignItems:"center"}}>
                <div style={{fontWeight:800, fontSize:15}}>{c.id}</div>
                <Estado estado={c.estado}/>
              </div>
              <div style={{fontSize:13, color:C.sub, margin:"6px 0 10px", lineHeight:1.5, overflow:"hidden", textOverflow:"ellipsis",
                display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical"}}>
                {c.descripcion || "Sin descripción"}
              </div>
              <div style={{display:"flex", justifyContent:"space-between", gap:10, flexWrap:"wrap", alignItems:"flex-end", fontSize:12, color:C.sub}}>
                <div style={{display:"flex", gap:14, flexWrap:"wrap"}}>
                  <span><Calendar size={11} aria-hidden="true"/> Emitida {fmtFecha(c.fecha)}</span>
                  <span><Clock size={11} aria-hidden="true"/> Vence {fmtFecha(c.vence)}</span>
                  {c.solicitudes_abiertas > 0 && <span style={{color:C.purple, fontWeight:600}}>Cambios solicitados</span>}
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:11}}>Neto {fmtCLP(c.neto)}{Number(c.iva) > 0 ? ` · IVA ${fmtCLP(c.iva)}` : " · sin IVA"}</div>
                  <div style={{fontSize:17, fontWeight:800, color:C.text}}>{fmtCLP(c.total)}</div>
                </div>
              </div>
              {c.puede_responder && (
                <div style={{marginTop:10, fontSize:12, fontWeight:700, color:"#92400e", background:C.warnL, borderRadius:8, padding:"6px 10px"}}>
                  Espera tu revisión · Abrir para aprobar, rechazar o pedir cambios
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Aviso({ aviso, onCerrar }) {
  const err = aviso.tipo === "error";
  return (
    <div role={err ? "alert" : "status"} style={{display:"flex", alignItems:"center", gap:8, padding:"10px 14px", marginBottom:14, borderRadius:10,
      fontSize:13, fontWeight:500, background: err ? C.errL : C.okL, color: err ? C.err : "#047857", border:`1px solid ${(err ? C.err : C.ok)}30`}}>
      {err ? <AlertCircle size={14}/> : <CheckCircle2 size={14}/>} <span style={{flex:1}}>{aviso.texto}</span>
      {onCerrar && <button type="button" onClick={onCerrar} aria-label="Cerrar aviso" style={{background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:16}}>×</button>}
    </div>
  );
}

// ── Detalle ──────────────────────────────────────────────────────────────────
function DetalleCotizacion({ id, onVolver, onCambio, onAviso, aviso }) {
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);          // 'aprobar' | 'rechazar' | 'cambios'

  const cargar = useCallback(async () => {
    setCargando(true); setError("");
    try { setD(await detalleCotizacion(id)); }
    catch (e) { setError(mensajeErrorCotizacion(e)); setD(null); }
    finally { setCargando(false); }
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const volver = (
    <button type="button" onClick={onVolver}
      style={{display:"inline-flex", alignItems:"center", gap:5, background:"none", border:"none", color:C.accent, fontWeight:700,
        fontSize:13, cursor:"pointer", padding:0, marginBottom:14, fontFamily:font}}>
      <ChevronLeft size={15}/> Volver a mis cotizaciones
    </button>
  );

  if (cargando && !d) return <div>{volver}<div style={{...card, padding:28, textAlign:"center", color:C.sub}}><Loader size={14} className="spinning"/> Cargando cotización…</div></div>;
  if (error || !d) return <div>{volver}<Aviso aviso={{ tipo:"error", texto: error || "No encontramos esta cotización." }}/></div>;

  const conIva = d.con_iva !== false;
  const resultado = async (r) => {
    setModal(null);
    onAviso({ tipo: "ok", texto:
      r.accion === "aprobada" ? `Cotización ${d.id} aprobada. El equipo de TuAplicacion fue notificado.`
      : r.accion === "rechazada" ? `Cotización ${d.id} rechazada. Gracias por avisarnos.`
      : "Recibimos tus observaciones. El equipo las revisará y te enviará una nueva versión si corresponde." });
    Promise.resolve().then(() => notificarRespuesta(r)).catch(() => {});   // sin await: no condiciona la confirmación
    await cargar(); onCambio?.();
  };

  return (
    <div>
      {volver}
      {aviso && <Aviso aviso={aviso} onCerrar={() => onAviso(null)}/>}

      {d.puede_responder && (
        <div style={{...card, padding:"14px 16px", marginBottom:14, background:C.warnL, border:`1px solid ${C.warn}50`}}>
          <div style={{fontWeight:700, color:"#92400e", marginBottom:10, fontSize:14}}>Esta cotización espera tu decisión</div>
          <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
            <button type="button" onClick={() => setModal("aprobar")} style={btn(C.ok)}><Check size={14}/> Aprobar cotización</button>
            <button type="button" onClick={() => setModal("cambios")} style={btn(C.purple)}><MessageSquare size={14}/> Solicitar cambios</button>
            <button type="button" onClick={() => setModal("rechazar")} style={btn(C.err, true)}><X size={14}/> Rechazar cotización</button>
          </div>
        </div>
      )}

      <div style={{...card, padding:"18px 20px", marginBottom:14}}>
        <div style={{display:"flex", justifyContent:"space-between", gap:10, flexWrap:"wrap", alignItems:"center"}}>
          <h2 style={{margin:0, fontSize:20, fontWeight:800}}>Cotización {d.id}</h2>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            {d.version > 1 && <span style={{fontSize:11, color:C.sub, fontWeight:600}}>Versión {d.version}</span>}
            <Estado estado={d.estado}/>
          </div>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:12, marginTop:14, fontSize:13}}>
          <Dato k="Cliente" v={d.cliente}/>
          {d.rut && <Dato k="RUT" v={d.rut}/>}
          <Dato k="Fecha de emisión" v={fmtFecha(d.fecha)}/>
          <Dato k="Vencimiento" v={fmtFecha(d.vence)}/>
        </div>
      </div>

      <div style={{...card, marginBottom:14, overflow:"hidden"}}>
        <div style={{padding:"12px 20px", borderBottom:`1px solid ${C.border}`, fontWeight:700, fontSize:14}}>Detalle de servicios</div>
        {(d.items || []).map((it, i) => (
          <div key={i} style={{display:"grid", gridTemplateColumns:"minmax(0,1fr) auto", gap:"4px 16px", padding:"12px 20px", borderBottom:`1px solid ${C.border}`}}>
            <div>
              <div style={{fontWeight:600, fontSize:14}}>{it.desc}</div>
              <div style={{fontSize:12, color:C.sub, marginTop:2}}>{it.tipo ? `${it.tipo} · ` : ""}{Number(it.horas)} h × {fmtCLP(it.ph)}/h</div>
            </div>
            <div style={{fontWeight:700, fontSize:14, textAlign:"right"}}>{fmtCLP(it.total)}</div>
          </div>
        ))}
        <div style={{padding:"14px 20px", background:C.bg, display:"grid", gap:6, fontSize:13}}>
          <Fila k={conIva ? "Subtotal neto" : "Total neto"} v={fmtCLP(d.neto)}/>
          {conIva && <Fila k="IVA 19%" v={fmtCLP(d.iva)}/>}
          <Fila k={conIva ? "Total" : "Total (sin IVA)"} v={fmtCLP(d.total)} fuerte/>
        </div>
      </div>

      {(d.notas || d.condiciones) && (
        <div style={{...card, padding:"16px 20px", marginBottom:14, display:"grid", gap:12, fontSize:13, lineHeight:1.6}}>
          {d.notas && <div><div style={{fontWeight:700, marginBottom:3}}>Notas</div><div style={{color:C.sub, whiteSpace:"pre-wrap"}}>{d.notas}</div></div>}
          {d.condiciones && <div><div style={{fontWeight:700, marginBottom:3}}>Condiciones comerciales</div><div style={{color:C.sub, whiteSpace:"pre-wrap"}}>{d.condiciones}</div></div>}
        </div>
      )}

      {d.proyectos?.length > 0 && (
        <div style={{...card, padding:"14px 20px", marginBottom:14, fontSize:13}}>
          <FolderOpen size={14} color={C.accent} style={{verticalAlign:"-2px"}} aria-hidden="true"/> Proyecto asociado: {d.proyectos.map(p => p.nombre).join(", ")}
        </div>
      )}

      <div style={{...card, padding:"16px 20px"}}>
        <div style={{fontWeight:700, fontSize:14, marginBottom:10}}>Comentarios y observaciones</div>
        {!d.respuestas?.length ? <div style={{fontSize:13, color:C.sub}}>Todavía no hay respuestas registradas.</div> : (
          <div style={{display:"grid", gap:10}}>
            {d.respuestas.map(r => {
              const a = ACCIONES_RESPUESTA[r.accion] || { label: r.accion, color: C.sub };
              return (
                <div key={r.id} style={{borderLeft:`3px solid ${a.color}`, paddingLeft: 12, fontSize:13}}>
                  <div style={{fontWeight:700, color:a.color}}>{a.label}{r.propia ? " · tú" : ""}
                    <span style={{fontWeight:400, color:C.light}}> · {fmtFechaHora(r.created_at)} · versión {r.version}</span>
                    {r.accion === "cambios" && <span style={{marginLeft:6, color:C.sub, fontWeight:400}}>{r.atendida ? "(atendida)" : "(en revisión)"}</span>}
                  </div>
                  {r.comentario && <div style={{color:C.sub, marginTop:2, whiteSpace:"pre-wrap"}}>{r.comentario}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal && <ModalRespuesta tipo={modal} d={d} onCerrar={() => setModal(null)} onListo={resultado} onRecargar={cargar}/>}
    </div>
  );
}

const Dato = ({ k, v }) => <div><div style={{fontSize:11, color:C.light, fontWeight:700, textTransform:"uppercase", letterSpacing:.4}}>{k}</div><div style={{fontWeight:600, marginTop:2}}>{v || "—"}</div></div>;
const Fila = ({ k, v, fuerte }) => <div style={{display:"flex", justifyContent:"space-between", fontWeight: fuerte ? 800 : 500, fontSize: fuerte ? 17 : 13, color: fuerte ? C.text : C.sub}}><span>{k}</span><span>{v}</span></div>;
const btn = (color, outline = false) => ({
  display:"inline-flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer",
  fontFamily:font, border:`1px solid ${color}`, background: outline ? C.white : color, color: outline ? color : "#fff",
});

// ── Confirmación de la respuesta ────────────────────────────────────────────
const TEXTOS = {
  aprobar:  { titulo: "Aprobar cotización", ok: "Sí, aprobar cotización", color: C.ok,
              ayuda: "Opcional: agrega un comentario para el equipo." },
  rechazar: { titulo: "Rechazar cotización", ok: "Rechazar cotización", color: C.err,
              ayuda: "Cuéntanos el motivo del rechazo (obligatorio)." },
  cambios:  { titulo: "Solicitar cambios", ok: "Enviar solicitud", color: C.purple,
              ayuda: "Describe los cambios que necesitas: alcance, servicios, precios, plazos u otras observaciones (obligatorio)." },
};

function ModalRespuesta({ tipo, d, onCerrar, onListo, onRecargar }) {
  const t = TEXTOS[tipo];
  const [coment, setComent] = useState("");
  const [acepto, setAcepto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const requiereTexto = tipo !== "aprobar";
  const listo = !enviando && (tipo === "aprobar" ? acepto : coment.trim().length >= 3);

  async function enviar(e) {
    e.preventDefault();
    if (!listo) return;
    setEnviando(true); setError("");
    try {
      const r = await responderCotizacion({ id: d.id, accion: tipo, comentario: coment.trim(), hash: d.contenido_hash });
      onListo(r);
    } catch (err) {
      setError(err.codigo ? err.message : mensajeErrorCotizacion(err));
      // El estado o el contenido cambiaron: mostrar la versión vigente.
      if (["contenido_cambio", "no_pendiente", "vencida", "no_encontrada"].includes(err.codigo)) { setEnviando(false); await onRecargar(); return; }
      setEnviando(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="resp-titulo"
      style={{position:"fixed", inset:0, zIndex:9997, background:"rgba(15,23,42,0.45)", display:"flex", alignItems:"flex-start",
        justifyContent:"center", padding:"24px 16px", overflowY:"auto"}}>
      <form onSubmit={enviar} style={{background:C.white, borderRadius:14, width:"100%", maxWidth:480, padding:"22px 24px",
        border:`1px solid ${C.border}`, boxShadow:"0 24px 80px #00000030", fontFamily:font}}>
        <h3 id="resp-titulo" style={{margin:"0 0 12px", fontSize:17}}>{t.titulo}</h3>

        <div style={{background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", marginBottom:14, fontSize:13, display:"grid", gap:4}}>
          <div style={{display:"flex", justifyContent:"space-between"}}><span style={{color:C.sub}}>Cotización</span><strong>{d.id}</strong></div>
          <div style={{display:"flex", justifyContent:"space-between"}}><span style={{color:C.sub}}>Neto</span><span>{fmtCLP(d.neto)}</span></div>
          {Number(d.iva) > 0 && <div style={{display:"flex", justifyContent:"space-between"}}><span style={{color:C.sub}}>IVA</span><span>{fmtCLP(d.iva)}</span></div>}
          <div style={{display:"flex", justifyContent:"space-between", fontSize:16}}><span style={{fontWeight:700}}>Total</span><strong>{fmtCLP(d.total)}</strong></div>
        </div>

        <label htmlFor="resp-comentario" style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:5}}>{t.ayuda}</label>
        <textarea id="resp-comentario" ref={ref} value={coment} maxLength={2000} rows={4} disabled={enviando}
          onChange={e => setComent(e.target.value)} required={requiereTexto}
          style={{width:"100%", boxSizing:"border-box", padding:"9px 12px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:14, fontFamily:font, resize:"vertical"}}/>

        {tipo === "aprobar" && (
          <label style={{display:"flex", gap:9, alignItems:"flex-start", marginTop:12, fontSize:13, lineHeight:1.5, cursor:"pointer"}}>
            <input type="checkbox" checked={acepto} onChange={e => setAcepto(e.target.checked)} disabled={enviando} style={{marginTop:3}}/>
            <span>Confirmo que revisé la cotización {d.id} por <strong>{fmtCLP(d.total)}</strong> y autorizo su ejecución en las condiciones indicadas.</span>
          </label>
        )}

        {error && <div role="alert" style={{marginTop:12, color:C.err, background:C.errL, borderRadius:8, padding:"9px 12px", fontSize:13, display:"flex", gap:7}}>
          <AlertCircle size={14} style={{flexShrink:0, marginTop:1}}/> {error}</div>}

        <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:18, flexWrap:"wrap"}}>
          <button type="button" onClick={onCerrar} disabled={enviando}
            style={{padding:"9px 16px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.sub, cursor:"pointer", fontFamily:font}}>
            Cancelar
          </button>
          <button type="submit" disabled={!listo}
            style={{padding:"9px 18px", borderRadius:8, border:"none", fontWeight:700, fontFamily:font, color:"#fff",
              background: listo ? t.color : C.light, cursor: listo ? "pointer" : "not-allowed"}}>
            {enviando ? <><Loader size={13} className="spinning"/> Enviando…</> : t.ok}
          </button>
        </div>
      </form>
    </div>
  );
}

